import Dialog from '@app/src/components/Dialog'
import { useAccount } from '@app/src/context/AccountContext'
import type { AssessmentTemplate } from '@app/src/types/resourceExplorer'
import { formatLevelLabel } from '@app/src/utils/assessmentLevelTranslations'
import useProviderType from '@app/src/hooks/useProviderType'
import { palette } from '@app/src/theme/palette'
import { getDateWithoutTimeFromString } from '@app/src/utils/helpersTs'
import {
  AccessTimeOutlined,
  AssessmentOutlined,
  CategoryOutlined,
  PublishedWithChangesOutlined,
  StarOutlined,
  SubjectOutlined,
} from '@mui/icons-material'
import { Alert, Avatar, Box, Button, ListItem, ListItemIcon, ListItemText, Stack, Typography } from '@mui/material'
import React, { useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { ListItemCard } from '../../components/ListItemCard'
import ApplyToSection from '../../CreationSteps/ApplyToSection'
import {
  getDeadlineYearLabelId,
  getLevelsBelowTarget,
  getSupplierRiskSummaryLabelId,
  isTopTargetLevel,
} from '../expectationAutomationLogic'
import type { ExpectationAutomationFormData } from '../types'
import { ExpectationAutomationFormField } from '../types'

interface ExpectationSummaryStepProps {
  affectedCount: number | undefined
  isLoadingCount: boolean
  bannerMessageId?: string
  importantKpisCount: number
  isEditing?: boolean
  assessmentTemplate: AssessmentTemplate | undefined
}

export const ExpectationSummaryStep: React.FC<ExpectationSummaryStepProps> = ({
  affectedCount,
  isLoadingCount,
  bannerMessageId,
  importantKpisCount,
  isEditing,
  assessmentTemplate,
}) => {
  const { formatMessage, formatDate } = useIntl()
  const { account } = useAccount()
  const [isDescriptionDialogOpen, setIsDescriptionDialogOpen] = useState(false)
  const providerType = useProviderType()
  const { watch } = useFormContext<ExpectationAutomationFormData>()
  const targetLevel = watch(ExpectationAutomationFormField.TargetAssessmentLevel)
  const supplierRisk = watch(ExpectationAutomationFormField.HighestAllowedProviderRiskStatus)
  const deadlineType = watch(ExpectationAutomationFormField.DeadlineType)
  const customDueDate = watch(ExpectationAutomationFormField.CustomDueDate)

  const targetLevelText = formatLevelLabel(assessmentTemplate, targetLevel, formatMessage)

  const assessmentName = assessmentTemplate?.name ?? ''

  const levelsBelowTargetText = getLevelsBelowTarget(targetLevel)
    .map(level => formatLevelLabel(assessmentTemplate, level, formatMessage))
    .join(', ')

  const supplierRiskLabelId = getSupplierRiskSummaryLabelId(supplierRisk)
  const supplierRiskText = supplierRiskLabelId ? formatMessage({ id: supplierRiskLabelId }) : ''

  const expectationText = formatMessage(
    { id: 'form.createAutomation.summary.expectation.expectationDescription' },
    {
      level: targetLevelText,
      isTopLevel: String(isTopTargetLevel(targetLevel, assessmentTemplate?.levels)),
      assessmentName,
    },
  )

  const deadlineYearLabelId = getDeadlineYearLabelId(deadlineType)
  const deadlineText =
    deadlineType === 'custom'
      ? customDueDate
        ? formatDate(getDateWithoutTimeFromString(customDueDate), { year: 'numeric', month: 'long', day: 'numeric' })
        : ''
      : deadlineYearLabelId
        ? formatMessage({ id: deadlineYearLabelId })
        : ''

  return (
    <Stack spacing={3} marginBottom={5}>
      <Stack>
        <Typography variant="subtitle1" fontWeight={600}>
          {formatMessage({ id: 'form.createAutomation.summary.expectation.automationTrigger' })}
        </Typography>
        <Typography variant="body1" color="textSecondary">
          {formatMessage({ id: 'form.createAutomation.summary.whenThisHappens' })}
        </Typography>
        <Stack>
          <ListItem sx={{ py: 3, px: 0 }}>
            <ListItemIcon>
              <Avatar variant="circular" sx={{ bgcolor: 'info.light', color: palette.visualization[1] }}>
                <CategoryOutlined />
              </Avatar>
            </ListItemIcon>
            <ListItemText
              primary={formatMessage(
                { id: 'form.createAutomation.summary.expectation.companyIsAssessed' },
                { providerType },
              )}
            />
          </ListItem>
          <ListItemCard
            icon={<AssessmentOutlined />}
            iconBgColor="transparent"
            text={formatMessage({ id: 'form.createAutomation.summary.expectation.baseline' }, { assessmentName })}
            rightContent={levelsBelowTargetText}
          />
          <Typography variant="body2" textAlign="center" margin="8px">
            {formatMessage({ id: 'form.createAutomation.summary.expectation.and' })}
          </Typography>
          <ListItemCard
            icon={<AssessmentOutlined />}
            iconBgColor="transparent"
            text={formatMessage({ id: 'navbar.providerRisk.supplier' })}
            rightContent={supplierRiskText}
          />
        </Stack>
      </Stack>
      <Stack>
        <Typography variant="subtitle1" fontWeight={600}>
          {formatMessage({ id: 'form.createAutomation.summary.expectation.automationAction' })}
        </Typography>
        <Typography variant="body1" color="textSecondary">
          {formatMessage({ id: 'form.createAutomation.summary.doThis' })}
        </Typography>
        <ListItem sx={{ py: 3, px: 0 }}>
          <ListItemIcon>
            <Avatar variant="circular" sx={{ bgcolor: 'info.light', color: palette.visualization[1] }}>
              <CategoryOutlined />
            </Avatar>
          </ListItemIcon>
          <ListItemText primary={formatMessage({ id: 'form.createAutomation.summary.expectation.sendExpectation' })} />
        </ListItem>
        <Stack gap={1}>
          <ListItemCard
            icon={<PublishedWithChangesOutlined />}
            iconBgColor="transparent"
            text={formatMessage({ id: 'form.createAutomation.summary.expectation.expectation' })}
            rightContent={expectationText}
          />
          <ListItemCard
            icon={<SubjectOutlined />}
            iconBgColor="transparent"
            text={formatMessage({ id: 'expectation.summary.introductionSectionTitle' }, { providerType })}
            rightContent={
              <Button variant="outlined" size="small" onClick={() => setIsDescriptionDialogOpen(true)}>
                {formatMessage({ id: 'form.createAutomation.summary.expectation.descriptionButtonLabel' })}
              </Button>
            }
            rightContentBold={false}
          />
          <ListItemCard
            icon={<AccessTimeOutlined />}
            iconBgColor="transparent"
            text={formatMessage({ id: 'expectation.dueDate' })}
            rightContent={deadlineText}
          />
          <ListItemCard
            icon={<StarOutlined />}
            iconBgColor="transparent"
            text={formatMessage({ id: 'bulkCreateExpectation.steps.summary.importantKpis' })}
            rightContent={formatMessage(
              { id: 'bulkCreateExpectation.steps.summary.importantKpisSelected' },
              { count: importantKpisCount },
            )}
          />
        </Stack>
      </Stack>
      <Stack>
        <Alert severity="info">{formatMessage({ id: 'form.createAutomation.summary.expectation.alertText' })}</Alert>
      </Stack>
      <Stack sx={{ marginBottom: '2rem' }}>
        <ApplyToSection
          affectedCount={affectedCount}
          isLoadingCount={isLoadingCount}
          bannerMessageId={bannerMessageId}
          isEditing={isEditing}
          futureOnlyDescriptionId="form.createExpectationAutomation.applyTo.futureOnlyDescription"
          existingAndFutureDescriptionId="form.createExpectationAutomation.applyTo.existingAndFutureDescription"
          messageValues={{ assessmentName }}
        />
      </Stack>

      <Dialog
        open={isDescriptionDialogOpen}
        onClose={() => setIsDescriptionDialogOpen(false)}
        title={formatMessage(
          { id: 'bulkCreateExpectation.steps.summary.descriptionDialogTitle' },
          { providerType, count: affectedCount },
        )}
        content={
          <Stack spacing={2}>
            <Box borderRadius={2} p={2} bgcolor="grey.100">
              <Typography variant="body1" whiteSpace="pre-wrap">
                {formatMessage(
                  { id: 'expectation.introductionDescription' },
                  {
                    accessor: account?.organization?.name ?? '',
                    providerType,
                    level: targetLevelText,
                    assessmentName,
                  },
                )}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {formatMessage({ id: 'expectation.summary.introductionHelperText' }, { providerType })}
            </Typography>
          </Stack>
        }
        buttons={[
          {
            onClick: () => setIsDescriptionDialogOpen(false),
            label: formatMessage({ id: 'general.close' }),
            variant: 'contained',
          },
        ]}
      />
    </Stack>
  )
}
